import { useState, useCallback } from 'react';
import { fetchSquareInvoicesForReservations } from '../api/dispatchService';

/**
 * useSquareInvoices
 *
 * Square Invoice の取得・キャッシュを管理するフック。
 * ReservationList と DayListReservations で重複していたロジックを一元化。
 */
export function useSquareInvoices(targetStatuses: number[] = [1, 2, 3, 6]) {
  const [squareDataCache, setSquareDataCache] = useState<Record<string, any>>({});
  const [squareDataLoading, setSquareDataLoading] = useState<boolean>(false);

  /**
   * 予約リストを受け取り、対象ステータスの予約の Square Invoice を一括取得してキャッシュに追加する
   */
  const fetchSquareData = useCallback(async (reservationsList: any[]) => {
    if (!Array.isArray(reservationsList) || reservationsList.length === 0) return;

    const targetContactIds = reservationsList
      .filter((r: any) => targetStatuses.includes(Number(r.status)))
      .map((r: any) => r.contact_code || r.detail?.contact_id)
      .filter(Boolean);

    if (targetContactIds.length === 0) return;

    setSquareDataLoading(true);
    try {
      const result = await fetchSquareInvoicesForReservations(targetContactIds);
      setSquareDataCache((prev: Record<string, any>) => ({ ...prev, ...result }));
    } catch (err) {
      console.error('Failed to fetch Square invoices:', err);
    } finally {
      setSquareDataLoading(false);
    }
  }, [targetStatuses]);

  /**
   * contactId に対応する Square Invoice 配列をキャッシュから返す
   */
  const getSquareInvoices = useCallback((contactId: string) => {
    return squareDataCache[contactId] ?? [];
  }, [squareDataCache]);

  return {
    squareDataCache,
    squareDataLoading,
    fetchSquareData,
    getSquareInvoices,
    setSquareDataCache, // 手動キャッシュ更新用（支払状態変更時など）
  };
}
